import {
	CalendarIcon,
	CheckboxIcon,
	ChoiceSelectField,
	DashboardIcon,
	DividerIcon,
	DropdownIcon,
	Forms9BoxRating,
	FormsAverage,
	FormsCheckbox,
	FormsDataViewer,
	FormsDatePicker,
	FormsDivider,
	FormsExternalLink,
	FormsHeader,
	FormsHRReviewFlag,
	FormsImage,
	FormsInstructions,
	FormsNumberField,
	FormsParagraph,
	FormsRating,
	FormsReviewViewer,
	FormsSelectField,
	FormsTextArea,
	FormsTextField,
	HeaderIcon,
	HrFlagIcon,
	type IconProps,
	ImageIcon,
	LaunchIcon,
	ListUlIcon,
	LoopIcon,
	NumberIcon,
	ParagraphIcon,
	QuestionTemplateIcon,
	RatingAverageGroupIcon,
	ReportIcon,
	ReviewersListIcon,
	StarIcon,
	TextIcon,
	ViewEvaluationIcon,
} from '@viapeople/components'
import { Combine } from 'lucide-react'
import type React from 'react'
import { ContainerComponent } from '../components/formComponents/ContainerComponent'
import { FormsAggregationPreview } from '../components/formComponents/FormsAggregationPreview'
import { FormsExternalDataView } from '../components/formComponents/FormsExternalDataView'
import { RepeatableComponent } from '../components/formComponents/RepeatableComponent'
import type { FormComponentName, FormComponentType } from '../types'

export type ComponentCategory = 'layout' | 'fields'

export interface ComponentProperty {
	type: string
	value: string | number | boolean | null
}

export interface PropertyDefinition {
	name: string
	type:
		| 'text'
		| 'textarea'
		| 'richtextarea'
		| 'color'
		| 'select'
		| 'number'
		| 'boolean'
		| 'ratingConfig'
		| 'divider'
		| 'options'
		| 'textLimit'
		| 'nineBoxConfig'
		| 'nineBoxCellSize'
		| 'ratingComponentSelector'
		| 'aggregationSourceSelector'
		| 'aggregationTypeSelector'
		| 'datePicker'
		| 'choices'
		| 'imageUpload'
		| 'documentUpload'
		| 'defaultValueSelector'
		| 'conditionalConfig'
		| 'dataSourceConfig'
		| 'externalDataSelector'
	label?: string
	editorReferenceId?: string
	options?: Array<{ value: string; label: string }>
	mutuallyExclusive?: string
	// When false, BulkEditModal will not apply changes to this property across components.
	// Defaults to true (omitted means bulk-editable).
	bulkEditable?: boolean
}

export interface ComponentDefinition {
	id: string
	name: FormComponentName
	type: FormComponentType
	displayName: string
	icon: React.FC<IconProps>
	category: ComponentCategory
	// eslint-disable-next-line @typescript-eslint/no-explicit-any -- components have varying prop signatures beyond the base { component } prop
	component: React.FC<any>
	defaultProps: Record<string, unknown>
	propertyDefinitions?: PropertyDefinition[]
}

const createPermissionProperty = (): PropertyDefinition => ({
	name: 'permissionTemplateId',
	type: 'select',
	label: 'Permission Template',
})

const createTextProperty = (name: string, label: string): PropertyDefinition => ({
	name,
	type: 'text',
	label,
})

const createNumberProperty = (
	name: string,
	label: string,
	options?: Array<{ value: string; label: string }>,
): PropertyDefinition => ({
	name,
	type: 'number',
	label,
	options,
})

const createTextAreaProperty = (name: string, label: string): PropertyDefinition => ({
	name,
	type: 'textarea',
	label,
})

const createRichTextAreaProperty = (name: string, label: string): PropertyDefinition => ({
	name,
	type: 'richtextarea',
	label,
})

const createColorProperty = (name: string, label?: string): PropertyDefinition => ({
	name,
	type: 'color',
	label,
})

const createRatingConfigProperty = (name: string, label?: string): PropertyDefinition => ({
	name,
	type: 'ratingConfig',
	label,
})

const createDividerProperty = (): PropertyDefinition => ({
	name: 'divider',
	type: 'divider',
})

const createConditionalConfigProperty = (): PropertyDefinition => ({
	name: 'conditional',
	type: 'conditionalConfig',
	label: 'Conditional Visibility',
})

const createSelectProperty = (
	name: string,
	label: string,
	options: Array<{ value: string; label: string }>,
): PropertyDefinition => ({
	name,
	type: 'select',
	label,
	options,
})

const createOptionsProperty = (name: string, label: string): PropertyDefinition => ({
	name,
	type: 'options',
	label,
})

const createDefaultValueSelector = (name: string, label: string, editorReferenceId: string): PropertyDefinition => ({
	name,
	type: 'defaultValueSelector',
	label,
	editorReferenceId,
})

const createTextLimitProperty = (
	name: string,
	label: string,
	options: Array<{ value: string; label: string }>,
): PropertyDefinition => ({
	name,
	type: 'textLimit',
	label,
	options,
})

const createBooleanProperty = (name: string, label: string): PropertyDefinition => ({
	name,
	type: 'boolean',
	label,
})

const labelSizeOptions = [
	{ value: 'h1', label: 'H1' },
	{ value: 'h2', label: 'H2' },
	{ value: 'h3', label: 'H3' },
	{ value: 'h4', label: 'H4' },
	{ value: 'h5', label: 'H5' },
	{ value: 'h6', label: 'H6' },
	{ value: 'body', label: 'Body' },
]

const createTooltipEnabledProperty = (): PropertyDefinition => ({
	name: 'tooltipEnabled',
	type: 'boolean',
	label: 'Enable Tooltip',
})

const createTooltipTextProperty = (): PropertyDefinition => ({
	name: 'tooltipText',
	type: 'richtextarea',
	label: 'Tooltip Help Text',
})

const createExternalDataSelectorProperty = (name: string, label: string): PropertyDefinition => ({
	name,
	type: 'externalDataSelector',
	label,
})

export const componentRegistry: ComponentDefinition[] = [
	{
		id: 'section',
		name: 'container',
		type: 'container',
		displayName: 'Section',
		icon: DashboardIcon,
		category: 'layout',
		defaultProps: {
			isSection: true,
			label: 'Label',
			labelColor: '#1E293B',
			labelSize: 'body',
			labelWeight: 'semibold',
			backgroundColor: '',
			columns: 1,
		},
		component: ContainerComponent,
		propertyDefinitions: [
			createTextProperty('label', 'Section Label'),
			createColorProperty('labelColor', undefined),
			createSelectProperty('labelSize', 'Label Size', labelSizeOptions),
			createSelectProperty('labelWeight', 'Label Weight', [
				{ value: 'regular', label: 'Regular' },
				{ value: 'semibold', label: 'Semibold' },
				{ value: 'hidden', label: 'Hidden' },
			]),
			createColorProperty('backgroundColor', 'Background Color'),
			createSelectProperty('columns', 'Columns', [
				{ value: '1', label: '1' },
				{ value: '2', label: '2' },
				{ value: '3', label: '3' },
				{ value: '4', label: '4' },
			]),
			createPermissionProperty(),
			createDividerProperty(),
			createConditionalConfigProperty(),
		],
	},
	{
		id: 'question',
		name: 'container',
		type: 'container',
		displayName: 'Question',
		icon: QuestionTemplateIcon,
		category: 'layout',
		defaultProps: {
			label: 'Label',
			isSection: false,
			repeatable: false,
			description: '',
			labelColor: '#1E293B',
			labelSize: 'body',
			labelWeight: 'semibold',
			descriptionColor: '#64748B',
			backgroundColor: '',
			showResponseAverage: true,
		},
		component: ContainerComponent,
		propertyDefinitions: [
			createTextProperty('label', 'Question Label'),
			createColorProperty('labelColor', undefined),
			createSelectProperty('labelSize', 'Label Size', labelSizeOptions),
			createSelectProperty('labelWeight', 'Label Weight', [
				{ value: 'regular', label: 'Regular' },
				{ value: 'semibold', label: 'Semibold' },
				{ value: 'hidden', label: 'Hidden' },
			]),
			createTextAreaProperty('description', 'Question Description'),
			createColorProperty('descriptionColor', undefined),
			createColorProperty('backgroundColor', 'Background Color'),
			createSelectProperty('repeatable', 'Repeatable', [
				{ value: 'true', label: 'Yes' },
				{ value: 'false', label: 'No' },
			]),
			createBooleanProperty('showResponseAverage', 'Show Response Average'),
			createPermissionProperty(),
			createDividerProperty(),
			createConditionalConfigProperty(),
		],
	},
	{
		id: 'header',
		name: 'heading',
		type: 'display',
		displayName: 'Header',
		icon: HeaderIcon,
		category: 'layout',
		defaultProps: {
			label: 'Header',
			labelColor: '#1E293B',
			size: 'h2',
			backgroundColor: '',
		},
		component: FormsHeader,
		propertyDefinitions: [
			createTextProperty('label', 'Header Text'),
			createColorProperty('labelColor', 'Text Color'),
			{
				name: 'size',
				type: 'select',
				label: 'Header Size',
				options: [
					{ value: 'h1', label: 'H1' },
					{ value: 'h2', label: 'H2' },
					{ value: 'h3', label: 'H3' },
					{ value: 'h4', label: 'H4' },
					{ value: 'h5', label: 'H5' },
					{ value: 'h6', label: 'H6' },
				],
			},
			createColorProperty('backgroundColor', 'Background Color'),
			createPermissionProperty(),
			createDividerProperty(),
			createConditionalConfigProperty(),
		],
	},
	{
		id: 'instructions',
		name: 'instructions',
		type: 'display',
		displayName: 'Instructions',
		icon: ReviewersListIcon,
		category: 'layout',
		component: FormsInstructions,
		defaultProps: {
			label: 'Instructions',
			labelColor: '#1E293B',
			labelSize: 'body',
			labelWeight: 'semibold',
			backgroundColor: '',
			text: 'Type your instructions here',
			textColor: '#64748B',
		},
		propertyDefinitions: [
			createTextProperty('label', 'Instructions Label'),
			createColorProperty('labelColor', undefined),
			createSelectProperty('labelSize', 'Label Size', labelSizeOptions),
			createSelectProperty('labelWeight', 'Label Weight', [
				{ value: 'regular', label: 'Regular' },
				{ value: 'semibold', label: 'Semibold' },
				{ value: 'hidden', label: 'Hidden' },
			]),
			createColorProperty('backgroundColor', 'Background Color'),
			createRichTextAreaProperty('text', 'Instructions Text'),
			createColorProperty('textColor', undefined),
			createPermissionProperty(),
			createDividerProperty(),
			createConditionalConfigProperty(),
		],
	},
	{
		id: 'paragraph',
		name: 'paragraph',
		type: 'display',
		displayName: 'Paragraph',
		icon: ParagraphIcon,
		category: 'layout',
		component: FormsParagraph,
		defaultProps: {
			label: 'Label',
			labelColor: '#1E293B',
			labelSize: 'body',
			labelWeight: 'semibold',
			backgroundColor: '',
			text: 'Enter your text here',
			textColor: '#1E293B',
		},
		propertyDefinitions: [
			createTextProperty('label', 'Paragraph Label'),
			createColorProperty('labelColor', 'Label Color'),
			createSelectProperty('labelSize', 'Label Size', labelSizeOptions),
			createSelectProperty('labelWeight', 'Label Weight', [
				{ value: 'regular', label: 'Regular' },
				{ value: 'semibold', label: 'Semibold' },
				{ value: 'hidden', label: 'Hidden' },
			]),
			createColorProperty('backgroundColor', 'Background Color'),
			createRichTextAreaProperty('text', 'Text Content'),
			createPermissionProperty(),
			createDividerProperty(),
			createConditionalConfigProperty(),
		],
	},
	{
		id: 'rating',
		name: 'rating',
		type: 'input',
		displayName: 'Rating',
		icon: StarIcon,
		category: 'fields',
		component: FormsRating,
		defaultProps: {
			label: undefined,
			labelColor: '#1E293B',
			labelSize: 'body',
			labelWeight: 'regular',
			reportLabel: undefined,
			count: 5,
			defaultValue: '',
			options: [
				{ value: '1', valueLabel: 'Poor', labelColor: '#ef4444' },
				{ value: '2', valueLabel: 'Fair', labelColor: '#f97316' },
				{ value: '3', valueLabel: 'Average', labelColor: '#eab308' },
				{ value: '4', valueLabel: 'Good', labelColor: '#83E279' },
				{ value: '5', valueLabel: 'Excellent', labelColor: '#10b981' },
			],
			tooltipEnabled: false,
			tooltipText: '',
			showColorsBeforeSelection: false,
		},
		propertyDefinitions: [
			createTextProperty('label', 'Rating Label'),
			createColorProperty('labelColor', undefined),
			createSelectProperty('labelSize', 'Label Size', labelSizeOptions),
			createSelectProperty('labelWeight', 'Label Weight', [
				{ value: 'regular', label: 'Regular' },
				{ value: 'semibold', label: 'Semibold' },
				{ value: 'hidden', label: 'Hidden' },
			]),
			createRatingConfigProperty('count', 'Rating Count'),
			createRatingConfigProperty('defaultValue', 'Default Value'),
			createDividerProperty(),
			createRatingConfigProperty('options', 'Rating Options'),
			createDividerProperty(),
			createTooltipEnabledProperty(),
			createTooltipTextProperty(),
			createBooleanProperty('showColorsBeforeSelection', 'Display Rating Colors by Default'),
			createPermissionProperty(),
			createTextProperty('reportLabel', 'Report Label'),
			createDividerProperty(),
			createConditionalConfigProperty(),
		],
	},
	{
		id: 'checkbox',
		name: 'checkbox',
		type: 'input',
		displayName: 'Checkbox',
		icon: CheckboxIcon,
		category: 'fields',
		component: FormsCheckbox,
		defaultProps: {
			label: 'Label',
			labelColor: '#1E293B',
			labelSize: 'body',
			labelWeight: 'regular',
			checkboxColor: '#1E293B',
			defaultChecked: false,
			reportLabel: undefined,
			tooltipEnabled: false,
			tooltipText: '',
		},
		propertyDefinitions: [
			createTextProperty('label', 'Checkbox Label'),
			createColorProperty('labelColor', 'Label Color'),
			createSelectProperty('labelSize', 'Label Size', labelSizeOptions),
			createSelectProperty('labelWeight', 'Label Weight', [
				{ value: 'regular', label: 'Regular' },
				{ value: 'semibold', label: 'Semibold' },
				{ value: 'hidden', label: 'Hidden' },
			]),
			createColorProperty('checkboxColor', 'Checkbox Color'),
			createTooltipEnabledProperty(),
			createTooltipTextProperty(),
			createPermissionProperty(),
			createTextProperty('reportLabel', 'Report Label'),
			createDividerProperty(),
			createConditionalConfigProperty(),
		],
	},
	{
		id: 'text-field',
		name: 'text',
		type: 'input',
		displayName: 'Text Field',
		icon: TextIcon,
		category: 'fields',
		component: FormsTextField,
		defaultProps: {
			label: undefined,
			labelColor: '#1E293B',
			labelSize: 'body',
			labelWeight: 'regular',
			placeholder: 'Enter your text here',
			reportLabel: undefined,
			tooltipEnabled: false,
			tooltipText: '',
		},
		propertyDefinitions: [
			createTextProperty('label', 'Text Field Label'),
			createColorProperty('labelColor', 'Label Color'),
			createSelectProperty('labelSize', 'Label Size', labelSizeOptions),
			createSelectProperty('labelWeight', 'Label Weight', [
				{ value: 'regular', label: 'Regular' },
				{ value: 'semibold', label: 'Semibold' },
				{ value: 'hidden', label: 'Hidden' },
			]),
			createTextProperty('placeholder', 'Placeholder Text'),
			createTooltipEnabledProperty(),
			createTooltipTextProperty(),
			createPermissionProperty(),
			createTextProperty('reportLabel', 'Report Label'),
			createDividerProperty(),
			createConditionalConfigProperty(),
		],
	},
	{
		id: 'text-area',
		name: 'textarea',
		type: 'input',
		displayName: 'Text Area',
		icon: TextIcon,
		category: 'fields',
		component: FormsTextArea,
		defaultProps: {
			label: undefined,
			labelColor: '#1E293B',
			labelSize: 'body',
			labelWeight: 'regular',
			placeholder: 'Enter your text here',
			rows: 3,
			textLimit: { characters: 3000, words: 500, isWordLimit: true },
			reportLabel: undefined,
			tooltipEnabled: false,
			tooltipText: '',
		},
		propertyDefinitions: [
			createTextProperty('label', 'Text Area Label'),
			createColorProperty('labelColor', 'Label Color'),
			createSelectProperty('labelSize', 'Label Size', labelSizeOptions),
			createSelectProperty('labelWeight', 'Label Weight', [
				{ value: 'regular', label: 'Regular' },
				{ value: 'semibold', label: 'Semibold' },
				{ value: 'hidden', label: 'Hidden' },
			]),
			createTextProperty('placeholder', 'Placeholder Text'),
			createNumberProperty('rows', 'Rows'),
			createTextLimitProperty('textLimit', 'Text Limit', [
				{ value: 'words', label: 'Words Limit' },
				{ value: 'characters', label: 'Characters Limit' },
			]),
			createTooltipEnabledProperty(),
			createTooltipTextProperty(),
			createPermissionProperty(),
			createTextProperty('reportLabel', 'Report Label'),
			createDividerProperty(),
			createConditionalConfigProperty(),
		],
	},
	{
		id: 'number-field',
		name: 'number',
		type: 'input',
		displayName: 'Number Field',
		icon: NumberIcon,
		category: 'fields',
		component: FormsNumberField,
		defaultProps: {
			label: undefined,
			labelColor: '#1E293B',
			labelSize: 'body',
			labelWeight: 'regular',
			prefix: '',
			suffix: '',
			decimals: 0,
			reportLabel: undefined,
			tooltipEnabled: false,
			tooltipText: '',
		},
		propertyDefinitions: [
			createTextProperty('label', 'Number Field Label'),
			createColorProperty('labelColor', 'Label Color'),
			createSelectProperty('labelSize', 'Label Size', labelSizeOptions),
			createSelectProperty('labelWeight', 'Label Weight', [
				{ value: 'regular', label: 'Regular' },
				{ value: 'semibold', label: 'Semibold' },
				{ value: 'hidden', label: 'Hidden' },
			]),
			createTextProperty('prefix', 'Prefix Text'),
			createTextProperty('suffix', 'Suffix Text'),
			createTooltipEnabledProperty(),
			createTooltipTextProperty(),
			createPermissionProperty(),
			createTextProperty('reportLabel', 'Report Label'),
			createDividerProperty(),
			createConditionalConfigProperty(),
		],
	},
	{
		id: 'date-picker',
		name: 'date',
		type: 'input',
		displayName: 'Date Picker',
		icon: CalendarIcon,
		category: 'fields',
		component: FormsDatePicker,
		defaultProps: {
			label: 'Select Date',
			labelColor: '#1E293B',
			labelSize: 'body',
			labelWeight: 'regular',
			allowPastDates: false,
			maximumDate: null,
			reportLabel: undefined,
			tooltipEnabled: false,
			tooltipText: '',
		},
		propertyDefinitions: [
			createTextProperty('label', 'Date Field Label'),
			createColorProperty('labelColor', 'Label Color'),
			createSelectProperty('labelSize', 'Label Size', labelSizeOptions),
			createSelectProperty('labelWeight', 'Label Weight', [
				{ value: 'regular', label: 'Regular' },
				{ value: 'semibold', label: 'Semibold' },
				{ value: 'hidden', label: 'Hidden' },
			]),
			createBooleanProperty('allowPastDates', 'Allow Past Dates'),
			{
				name: 'maximumDate',
				type: 'datePicker',
				label: 'Maximum Date',
			},
			createTooltipEnabledProperty(),
			createTooltipTextProperty(),
			createPermissionProperty(),
			createTextProperty('reportLabel', 'Report Label'),
			createDividerProperty(),
			createConditionalConfigProperty(),
		],
	},
	{
		id: 'radio-group',
		name: 'radio-group',
		type: 'input',
		displayName: 'Choices',
		icon: ListUlIcon,
		category: 'fields',
		component: ChoiceSelectField,
		defaultProps: {
			label: 'Select an option:',
			labelColor: '#1E293B',
			labelSize: 'body',
			labelWeight: 'regular',
			reportLabel: undefined,
			options: [],
			defaultChoice: '',
			tooltipEnabled: false,
			tooltipText: '',
		},
		propertyDefinitions: [
			createTextProperty('label', 'Select Field Label'),
			createSelectProperty('labelSize', 'Label Size', labelSizeOptions),
			createSelectProperty('labelWeight', 'Label Weight', [
				{ value: 'regular', label: 'Regular' },
				{ value: 'semibold', label: 'Semibold' },
				{ value: 'hidden', label: 'Hidden' },
			]),
			createOptionsProperty('options', 'Options'),
			createDefaultValueSelector('defaultChoice', 'Default Choice', 'options'),
			createTooltipEnabledProperty(),
			createTooltipTextProperty(),
			createPermissionProperty(),
			createTextProperty('reportLabel', 'Report Label'),
			createDividerProperty(),
			createConditionalConfigProperty(),
		],
	},
	{
		id: 'select-field',
		name: 'select',
		type: 'input',
		displayName: 'Dropdown',
		icon: DropdownIcon,
		category: 'fields',
		component: FormsSelectField,
		defaultProps: {
			label: undefined,
			labelColor: '#1E293B',
			labelSize: 'body',
			labelWeight: 'regular',
			placeholder: 'Select an option',
			options: [{ label: '', value: '' }],
			reportLabel: undefined,
			tooltipEnabled: false,
			tooltipText: '',
		},
		propertyDefinitions: [
			createTextProperty('label', 'Select Field Label'),
			createColorProperty('labelColor', 'Label Color'),
			createSelectProperty('labelSize', 'Label Size', labelSizeOptions),
			createSelectProperty('labelWeight', 'Label Weight', [
				{ value: 'regular', label: 'Regular' },
				{ value: 'semibold', label: 'Semibold' },
				{ value: 'hidden', label: 'Hidden' },
			]),
			createTextProperty('placeholder', 'Placeholder Text'),
			createOptionsProperty('options', 'Options'),
			createTooltipEnabledProperty(),
			createTooltipTextProperty(),
			createPermissionProperty(),
			createTextProperty('reportLabel', 'Report Label'),
			createDividerProperty(),
			createConditionalConfigProperty(),
		],
	},
	{
		id: 'divider',
		name: 'divider',
		type: 'display',
		displayName: 'Divider',
		icon: DividerIcon,
		category: 'layout',
		component: FormsDivider,
		defaultProps: { label: 'divider' },
		// TODO: Remove propertyDefinitions after api is updated
		propertyDefinitions: [createPermissionProperty()],
	},
	{
		id: 'review-viewer',
		name: 'reviewviewer',
		type: 'display',
		displayName: 'Report Viewer',
		icon: ReportIcon,
		category: 'layout',
		component: FormsReviewViewer,
		defaultProps: {
			label: 'Report Viewer',
			labelColor: '#1E293B',
			linkText: 'View 2024 Year-End Performance Evaluation',
			reviewSegment: 'EmployeeView',
		},
		propertyDefinitions: [
			createTextProperty('linkText', 'Link Text'),
			createSelectProperty('reviewSegment', 'Report Segment', [
				{ value: 'EmployeeView', label: 'Employee View' },
				{ value: 'CA_Complete', label: 'CA Complete' },
				{ value: 'MGR_Complete', label: 'Manager Complete' },
			]),
			createPermissionProperty(),
			createDividerProperty(),
			createConditionalConfigProperty(),
		],
	},
	{
		id: 'external-link',
		name: 'externallink',
		type: 'display',
		displayName: 'External Link',
		icon: LaunchIcon,
		category: 'layout',
		component: FormsExternalLink,
		defaultProps: {
			label: 'External Link',
			labelColor: '#1E293B',
			url: '',
			linkText: 'Click here',
		},
		propertyDefinitions: [
			{ name: 'document', type: 'documentUpload', label: 'Document (PDF / DOC / DOCX, optional)' },
			createTextProperty('url', 'URL (used if no document uploaded)'),
			createTextProperty('linkText', 'Link Text'),
			createPermissionProperty(),
			createDividerProperty(),
			createConditionalConfigProperty(),
		],
	},
	{
		id: '9box-rating',
		name: '9boxrating',
		type: 'input',
		displayName: '9-Box Rating',
		icon: StarIcon,
		category: 'fields',
		component: Forms9BoxRating,
		defaultProps: {
			label: 'Performance vs Potential',
			labelColor: '#1E293B',
			labelSize: 'body',
			labelWeight: 'regular',
			xAxisLabel: 'Potential',
			yAxisLabel: 'Performance',
			selectionColor: '#3e3e3e',
			boxDefinitions: {
				1: { label: 'Bad Hire', color: '#ef4444' },
				2: { label: 'Backup', color: '#ef4444' },
				3: { label: 'Workhorse', color: '#eab308' },
				4: { label: 'Inconsistent', color: '#ef4444' },
				5: { label: 'Core Player', color: '#eab308' },
				6: { label: 'High Performer', color: '#3b82f6' },
				7: { label: 'Potential Gem', color: '#eab308' },
				8: { label: 'High Potential', color: '#3b82f6' },
				9: { label: 'Star', color: '#3b82f6' },
			},
			reportLabel: undefined,
			tooltipEnabled: false,
			tooltipText: '',
		},
		propertyDefinitions: [
			createTextProperty('label', '9-Box Rating Label'),
			createColorProperty('labelColor', 'Label Color'),
			createSelectProperty('labelSize', 'Label Size', labelSizeOptions),
			createSelectProperty('labelWeight', 'Label Weight', [
				{ value: 'regular', label: 'Regular' },
				{ value: 'semibold', label: 'Semibold' },
				{ value: 'hidden', label: 'Hidden' },
			]),
			createTextProperty('xAxisLabel', 'X-Axis Label'),
			createTextProperty('yAxisLabel', 'Y-Axis Label'),
			createColorProperty('selectionColor', 'Selection Color'),
			createDividerProperty(),
			{
				name: 'boxDefinitions',
				type: 'nineBoxConfig',
				label: '9-Box Configuration',
			},
			{
				name: 'cellSize',
				type: 'nineBoxCellSize',
				label: 'Cell Size',
				// Per-instance sizing — must not be silently overwritten across all 9-boxes via Bulk Edit.
				bulkEditable: false,
			},
			createDividerProperty(),
			createTooltipEnabledProperty(),
			createTooltipTextProperty(),
			createPermissionProperty(),
			createTextProperty('reportLabel', 'Report Label'),
			createDividerProperty(),
			createConditionalConfigProperty(),
		],
	},
	{
		id: 'average',
		name: 'average',
		type: 'display',
		displayName: 'Average',
		icon: RatingAverageGroupIcon,
		category: 'fields',
		component: FormsAverage,
		defaultProps: {
			label: 'Average',
			labelColor: '#1E293B',
			labelSize: 'body',
			labelWeight: 'semibold',
			prefix: '',
			suffix: '',
			decimals: 2,
			selectedComponentIds: [],
			averageSection: false,
			excludeParticipants: false,
			selfRatingsOnly: false,
			reportLabel: undefined,
		},
		propertyDefinitions: [
			createTextProperty('label', 'Average Label'),
			createColorProperty('labelColor', 'Label Color'),
			createSelectProperty('labelSize', 'Label Size', labelSizeOptions),
			createSelectProperty('labelWeight', 'Label Weight', [
				{ value: 'regular', label: 'Regular' },
				{ value: 'semibold', label: 'Semibold' },
				{ value: 'hidden', label: 'Hidden' },
			]),
			createTextProperty('prefix', 'Prefix'),
			createTextProperty('suffix', 'Suffix'),
			createNumberProperty('decimals', 'Decimal Places', [{ value: '20', label: 'maxLimit' }]),
			createDividerProperty(),
			createBooleanProperty('averageSection', 'Average Current Section'),
			{
				name: 'selectedComponentIds',
				type: 'ratingComponentSelector',
				label: 'Select Components to Average',
			},
			createDividerProperty(),
			{
				...createBooleanProperty('excludeParticipants', 'Exclude participants (Self) from Average'),
				mutuallyExclusive: 'selfRatingsOnly',
			},
			{
				...createBooleanProperty('selfRatingsOnly', 'Include only self ratings (Participant) in Average'),
				mutuallyExclusive: 'excludeParticipants',
			},
			createPermissionProperty(),
			createTextProperty('reportLabel', 'Report Label'),
			createDividerProperty(),
			createConditionalConfigProperty(),
		],
	},
	{
		id: 'repeatable',
		name: 'repeatable',
		type: 'container',
		displayName: 'Repeatable',
		icon: LoopIcon,
		category: 'layout',
		component: RepeatableComponent,
		defaultProps: {
			label: 'Label',
			labelColor: '#1E293B',
			labelSize: 'body',
			labelWeight: 'semibold',
			backgroundColor: '',
			columns: 1,
			min: 1,
			max: undefined,
			buttonLabel: '+ Add',
			instanceLabel: 'Instance',
		},
		propertyDefinitions: [
			createTextProperty('label', 'Repeatable Label'),
			createColorProperty('labelColor', 'Label Color'),
			createSelectProperty('labelSize', 'Label Size', labelSizeOptions),
			createSelectProperty('labelWeight', 'Label Weight', [
				{ value: 'regular', label: 'Regular' },
				{ value: 'semibold', label: 'Semibold' },
				{ value: 'hidden', label: 'Hidden' },
			]),
			createColorProperty('backgroundColor', 'Background Color'),
			createSelectProperty('columns', 'Columns', [
				{ value: '1', label: '1' },
				{ value: '2', label: '2' },
				{ value: '3', label: '3' },
				{ value: '4', label: '4' },
			]),
			createNumberProperty('min', 'Minimum Instances'),
			createNumberProperty('max', 'Maximum Instances'),
			createTextProperty('buttonLabel', 'Add Button Label'),
			createTextProperty('instanceLabel', 'Instance Label'),
			createPermissionProperty(),
			createDividerProperty(),
			createConditionalConfigProperty(),
		],
	},
	{
		id: 'image',
		name: 'image',
		type: 'display',
		displayName: 'Image',
		icon: ImageIcon,
		category: 'layout',
		component: FormsImage,
		defaultProps: {
			label: '',
			labelColor: '#1E293B',
			labelSize: 'body',
			labelWeight: 'regular',
			backgroundColor: '',
			imageUrl: '',
			caption: '',
			displayWidth: 50,
		},
		propertyDefinitions: [
			createTextProperty('label', 'Label (optional)'),
			createColorProperty('labelColor', 'Label Color'),
			createSelectProperty('labelSize', 'Label Size', labelSizeOptions),
			createSelectProperty('labelWeight', 'Label Weight', [
				{ value: 'regular', label: 'Regular' },
				{ value: 'semibold', label: 'Semibold' },
				{ value: 'hidden', label: 'Hidden' },
			]),
			createColorProperty('backgroundColor', 'Background Color'),
			{
				name: 'imageUrl',
				type: 'imageUpload',
				label: 'Image',
			},
			createTextProperty('caption', 'Caption (Alt Text)'),
			createSelectProperty('displayWidth', 'Display Width', [
				{ value: '25', label: '25%' },
				{ value: '50', label: '50%' },
				{ value: '75', label: '75%' },
				{ value: '100', label: '100%' },
			]),
			createPermissionProperty(),
			createDividerProperty(),
			createConditionalConfigProperty(),
		],
	},
	{
		id: 'data-viewer',
		name: 'dataviewer',
		type: 'display',
		displayName: 'Data Viewer',
		icon: ViewEvaluationIcon,
		category: 'layout',
		component: FormsDataViewer,
		defaultProps: {
			label: 'Historical Data',
			labelColor: '#1E293B',
			labelSize: 'body',
			labelWeight: 'semibold',
			collapsible: false,
			defaultCollapsed: false,
			dataSources: [],
			excludeSelfResponses: false,
			excludeRaterResponses: false,
			excludePrimaryManagerResponses: false,
			excludeSkipManagerResponses: false,
		},
		propertyDefinitions: [
			createTextProperty('label', 'Label'),
			createColorProperty('labelColor', 'Label Color'),
			createSelectProperty('labelSize', 'Label Size', labelSizeOptions),
			createSelectProperty('labelWeight', 'Label Weight', [
				{ value: 'regular', label: 'Regular' },
				{ value: 'semibold', label: 'Semibold' },
				{ value: 'hidden', label: 'Hidden' },
			]),
			createDividerProperty(),
			{
				name: 'dataSources',
				type: 'dataSourceConfig',
				label: 'Data Sources',
			},
			createDividerProperty(),
			createBooleanProperty('collapsible', 'Collapsible'),
			createBooleanProperty('defaultCollapsed', 'Collapsed by Default'),
			createBooleanProperty('excludeSelfResponses', 'Exclude Self Responses'),
			createBooleanProperty('excludeRaterResponses', 'Exclude Rater Responses'),
			createBooleanProperty('excludePrimaryManagerResponses', 'Exclude Primary Manager Responses'),
			createBooleanProperty('excludeSkipManagerResponses', 'Exclude Skip Manager Responses'),
			createPermissionProperty(),
			createDividerProperty(),
			createConditionalConfigProperty(),
		],
	},
	{
		id: 'hr-review-flag',
		name: 'hrreviewflag',
		type: 'input',
		displayName: 'Widget: HR Review Flag',
		icon: HrFlagIcon,
		category: 'fields',
		component: FormsHRReviewFlag,
		defaultProps: {
			widgetLabel: 'HR Review Flag',
			toggleLabel: 'Request HR Review',
			helperText: 'Flag this submission for a confidential conversation with HR. Your manager will not be notified.',
			reasonRequired: true,
			reasonPrompt: 'What would you like to discuss?',
			reasonPlaceholder:
				'Briefly describe the topic you’d like to discuss with HR. This is confidential and will only be visible to the HR team…',
			showConfidentialityFooter: true,
			notifyHrOnFlag: true,
			notifyEmailTemplateId: '',
			confirmationMessage: 'A member of the HR team will reach out to schedule a conversation.',
		},
		propertyDefinitions: [
			createTextProperty('widgetLabel', 'Widget Label'),
			createTextProperty('toggleLabel', 'Toggle Label'),
			createTextAreaProperty('helperText', 'Helper Text'),
			createDividerProperty(),
			createBooleanProperty('reasonRequired', 'Require Reason When Flagged'),
			createTextProperty('reasonPrompt', 'Reason Prompt'),
			createTextProperty('reasonPlaceholder', 'Reason Placeholder'),
			createDividerProperty(),
			createBooleanProperty('showConfidentialityFooter', 'Show Lock Icon & Confidentiality Message'),
			createDividerProperty(),
			createBooleanProperty('notifyHrOnFlag', 'Email HR On New Flag'),
			{
				name: 'notifyEmailTemplateId',
				type: 'select',
				label: 'Notification Email Template',
				options: [],
				// editorReferenceId is read by DynamicPropertyEditor and forwarded as
				// referenceContext to the editor strategy. EmailTemplateSelect uses it
				// to gate display: hides itself when notifyHrOnFlag is false.
				editorReferenceId: 'notifyHrOnFlag',
			},
			createDividerProperty(),
			createTextAreaProperty('confirmationMessage', 'Confirmation Message'),
			createPermissionProperty(),
			createDividerProperty(),
			createConditionalConfigProperty(),
		],
	},
	{
		id: 'external-data-view',
		name: 'external_data_view',
		type: 'display',
		displayName: 'External Data View',
		icon: ReportIcon,
		category: 'layout',
		component: FormsExternalDataView,
		defaultProps: {
			label: 'External Data Profile',
			labelColor: '#1E293B',
			externalDataConfig: {
				fileId: '',
				filename: '',
				matchColumn: '',
				columnsToDisplay: [],
			},
		},
		propertyDefinitions: [
			createTextProperty('label', 'Header'),
			createColorProperty('labelColor', 'Header Color'),
			createExternalDataSelectorProperty('externalDataConfig', 'Data Configuration'),
			createPermissionProperty(),
		],
	},
	{
		id: 'aggregation',
		name: 'aggregation',
		type: 'display',
		displayName: 'Aggregation',
		icon: Combine,
		category: 'layout',
		component: FormsAggregationPreview,
		defaultProps: {
			label: 'Aggregation',
			labelColor: '#1E293B',
			labelSize: 'body',
			labelWeight: 'semibold',
			selectedComponentIds: [],
			aggregationType: '',
			grouping: 'combine',
		},
		propertyDefinitions: [
			createTextProperty('label', 'Aggregation Label'),
			createColorProperty('labelColor', 'Label Color'),
			createSelectProperty('labelSize', 'Label Size', labelSizeOptions),
			createSelectProperty('labelWeight', 'Label Weight', [
				{ value: 'regular', label: 'Regular' },
				{ value: 'semibold', label: 'Semibold' },
				{ value: 'hidden', label: 'Hidden' },
			]),
			createDividerProperty(),
			{
				name: 'selectedComponentIds',
				type: 'aggregationSourceSelector',
				label: 'Select Source',
			},
			{
				name: 'aggregationType',
				type: 'aggregationTypeSelector',
				label: 'Select Aggregation',
			},
			createSelectProperty('grouping', 'Grouping', [
				{ value: 'combine', label: 'Combine' },
				{ value: 'groupByRole', label: 'Group by Role' },
				{ value: 'groupByRoleAndRateGroup', label: 'Group by Role and Rate Group' },
			]),
			createPermissionProperty(),
		],
	},
]
